import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '@components/layout/Header';
import Footer from '@components/layout/Footer';
import { APP_NAME, ROUTES } from '@utils/constants';

const PrivacyPolicyPage: React.FC = () => {
  const lastUpdated = 'December 14, 2025';

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-1 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back link */}
          <Link
            to={ROUTES.HOME}
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          {/* Page header */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-gray-600">
              Last updated: {lastUpdated}
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-sm p-8 prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                {APP_NAME} ("we," "us," or "our") is committed to protecting your privacy. This Privacy
                Policy explains how we collect, use, disclose, and safeguard your information when you
                use our platform and services.
              </p>
              <p className="text-gray-700 mb-4">
                {APP_NAME} is a multi-tenant SaaS platform serving two types of users:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Photographers (B2B):</strong> Professional photographers who use our platform to showcase and sell photos</li>
                <li><strong>Customers (B2C):</strong> Individuals who browse and purchase photos from Photographer galleries</li>
              </ul>
              <p className="text-gray-700 mb-4">
                By using {APP_NAME}, you consent to the data practices described in this policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">2.1 Information You Provide</h3>

              <h4 className="text-lg font-medium text-gray-700 mb-2">For Photographers:</h4>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Account registration information (name, email, password)</li>
                <li>Business information (business name, address, tax ID for payments)</li>
                <li>Payment and banking information (processed securely by Stripe)</li>
                <li>Profile information (bio, profile photo, portfolio)</li>
                <li>Photos and media uploaded to the platform</li>
                <li>Event information and gallery details</li>
                <li>Communication preferences</li>
              </ul>

              <h4 className="text-lg font-medium text-gray-700 mb-2">For Customers:</h4>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Contact information (name, email)</li>
                <li>Payment information (processed securely by Stripe - we do not store card details)</li>
                <li>Order history and purchase details</li>
                <li>Communication preferences</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">2.2 Information Collected Automatically</h3>
              <p className="text-gray-700 mb-4">
                When you use our platform, we automatically collect:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Device Information:</strong> Browser type, operating system, device type</li>
                <li><strong>Usage Data:</strong> Pages visited, features used, time spent on platform</li>
                <li><strong>Log Data:</strong> IP address, access times, referring URLs</li>
                <li><strong>Cookies:</strong> Session cookies, preference cookies, analytics cookies</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">2.3 Information from Third Parties</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Stripe:</strong> Payment verification and fraud prevention data</li>
                <li><strong>Authentication Providers:</strong> If you sign in via social login (future feature)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">3.1 Core Service Operations</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Process and fulfill photo purchases</li>
                <li>Manage Photographer subscriptions and accounts</li>
                <li>Facilitate payments between Customers and Photographers</li>
                <li>Provide customer support</li>
                <li>Send transactional emails (order confirmations, receipts)</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">3.2 Platform Improvement</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Analyze usage patterns to improve our services</li>
                <li>Debug and fix technical issues</li>
                <li>Develop new features based on user feedback</li>
                <li>Monitor platform performance and security</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">3.3 Communications</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Send service updates and announcements</li>
                <li>Provide marketing communications (with your consent)</li>
                <li>Respond to inquiries and support requests</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">3.4 Legal and Safety</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Comply with legal obligations</li>
                <li>Enforce our Terms of Service</li>
                <li>Prevent fraud and abuse</li>
                <li>Protect rights and safety of users and third parties</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Multi-Tenant Data Isolation</h2>
              <p className="text-gray-700 mb-4">
                {APP_NAME} operates as a multi-tenant platform where multiple Photographers share the
                same infrastructure. We implement strict data isolation measures:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Logical Separation:</strong> Each Photographer's data is logically separated
                  using unique identifiers and access controls</li>
                <li><strong>Access Controls:</strong> Photographers can only access their own events,
                  photos, and customer data</li>
                <li><strong>Customer Data:</strong> Customer purchase data is associated with specific
                  Photographers and is not shared across tenants</li>
                <li><strong>Media Storage:</strong> Photos are stored with Photographer-specific prefixes
                  and access permissions</li>
              </ul>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 my-6">
                <p className="text-yellow-700">
                  <strong>Note:</strong> While we maintain strict data isolation, Customers should be aware
                  that when they purchase photos, their contact and order information is shared with the
                  relevant Photographer to fulfill the order.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Payment Information</h2>
              <p className="text-gray-700 mb-4">
                All payment processing is handled by <strong>Stripe</strong>, a PCI-DSS compliant payment
                processor. We want to be clear about how payment data is handled:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>We DO NOT store:</strong> Credit card numbers, CVV codes, or complete bank account details</li>
                <li><strong>We DO store:</strong> Last 4 digits of cards (for display purposes), transaction IDs, and payment status</li>
                <li><strong>Stripe stores:</strong> Full payment information securely according to PCI-DSS standards</li>
              </ul>
              <p className="text-gray-700 mb-4">
                For Photographers using Stripe Connect, additional identity verification may be required
                by Stripe to comply with financial regulations. This information is collected and stored
                by Stripe, not {APP_NAME}.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Photo Data and Storage</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">6.1 Photographer Uploads</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Photos are stored on Amazon Web Services (AWS) S3 with encryption at rest</li>
                <li>Multiple resolution versions are generated for preview and delivery</li>
                <li>Original files are preserved for full-resolution downloads</li>
                <li>Photos remain the intellectual property of the Photographer</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">6.2 Photo Metadata</h3>
              <p className="text-gray-700 mb-4">
                We may process photo metadata (EXIF data) to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Automatically organize photos by date</li>
                <li>Display camera settings if enabled by the Photographer</li>
                <li>Generate thumbnails and previews</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Photographers can choose to strip metadata from delivered photos for privacy.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">6.3 Data Retention</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Active accounts: Data retained while subscription is active</li>
                <li>Cancelled subscriptions: Data retained for 30 days post-cancellation</li>
                <li>Suspended accounts: Data retained for 60 days before deletion</li>
                <li>Customer order data: Retained for 7 years for legal/tax purposes</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Information Sharing</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">7.1 With Photographers</h3>
              <p className="text-gray-700 mb-4">
                When Customers purchase photos, we share necessary information with the Photographer:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Customer name and email</li>
                <li>Order details and purchase history</li>
                <li>This is necessary to provide the service and customer support</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">7.2 With Service Providers</h3>
              <p className="text-gray-700 mb-4">
                We share data with trusted third parties who assist in operating our platform:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Stripe:</strong> Payment processing</li>
                <li><strong>Amazon Web Services:</strong> Cloud hosting and storage</li>
                <li><strong>Email Service Providers:</strong> Transactional and marketing emails</li>
                <li><strong>Analytics Providers:</strong> Usage analytics (anonymized where possible)</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">7.3 Legal Requirements</h3>
              <p className="text-gray-700 mb-4">
                We may disclose information when required by law, court order, or government request,
                or to protect our rights, property, or safety.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">7.4 Business Transfers</h3>
              <p className="text-gray-700 mb-4">
                In the event of a merger, acquisition, or sale of assets, user data may be transferred
                as part of the business assets.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Cookies and Tracking</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">8.1 Types of Cookies We Use</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Essential Cookies:</strong> Required for basic platform functionality (authentication, cart)</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our platform</li>
                <li><strong>Marketing Cookies:</strong> Used for targeted advertising (with consent)</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">8.2 Managing Cookies</h3>
              <p className="text-gray-700 mb-4">
                You can control cookies through your browser settings. Note that disabling certain
                cookies may affect platform functionality.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Your Rights and Choices</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">9.1 Access and Portability</h3>
              <p className="text-gray-700 mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Access the personal data we hold about you</li>
                <li>Request a copy of your data in a portable format</li>
                <li>Review and update your account information</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">9.2 Correction and Deletion</h3>
              <p className="text-gray-700 mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Correct inaccurate personal data</li>
                <li>Request deletion of your personal data (subject to legal retention requirements)</li>
                <li>Delete your account through account settings</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">9.3 Marketing Communications</h3>
              <p className="text-gray-700 mb-4">
                You can opt out of marketing communications at any time by:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Clicking "unsubscribe" in any marketing email</li>
                <li>Updating preferences in your account settings</li>
                <li>Contacting us at privacy@n3rd-media.com</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Encryption:</strong> TLS/SSL encryption for data in transit, AES-256 for data at rest</li>
                <li><strong>Access Controls:</strong> Role-based access, multi-factor authentication available</li>
                <li><strong>Infrastructure:</strong> AWS security best practices, regular security audits</li>
                <li><strong>Monitoring:</strong> 24/7 monitoring for suspicious activity</li>
              </ul>
              <p className="text-gray-700 mb-4">
                While we strive to protect your data, no method of transmission or storage is 100%
                secure. We cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. International Data Transfers</h2>
              <p className="text-gray-700 mb-4">
                {APP_NAME} is based in the United States. If you access our platform from outside the
                US, your data will be transferred to and processed in the United States. By using our
                platform, you consent to this transfer.
              </p>
              <p className="text-gray-700 mb-4">
                We comply with applicable data protection laws and use appropriate safeguards for
                international data transfers.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                {APP_NAME} is not intended for children under 13 years of age. We do not knowingly
                collect personal information from children under 13. If you believe we have collected
                information from a child under 13, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. California Privacy Rights (CCPA)</h2>
              <p className="text-gray-700 mb-4">
                California residents have additional rights under the California Consumer Privacy Act:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Right to Know:</strong> Request disclosure of data collected, used, and shared</li>
                <li><strong>Right to Delete:</strong> Request deletion of personal information</li>
                <li><strong>Right to Opt-Out:</strong> Opt out of the sale of personal information (we do not sell personal data)</li>
                <li><strong>Non-Discrimination:</strong> Equal service regardless of privacy choices</li>
              </ul>
              <p className="text-gray-700 mb-4">
                To exercise these rights, contact us at privacy@n3rd-media.com.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Changes to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of material
                changes by:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Posting the updated policy on this page</li>
                <li>Updating the "Last Updated" date</li>
                <li>Sending email notification for significant changes</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Your continued use of {APP_NAME} after changes take effect constitutes acceptance of
                the updated policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                For questions or concerns about this Privacy Policy or our data practices:
              </p>
              <ul className="list-none text-gray-700 mb-4">
                <li><strong>Email:</strong> privacy@n3rd-media.com</li>
                <li><strong>Address:</strong> Pixel Share, San Francisco, CA</li>
              </ul>
              <p className="text-gray-700 mb-4">
                We will respond to privacy inquiries within 30 days.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Related Policies</h2>
              <ul className="list-disc pl-6 text-gray-700">
                <li><Link to={ROUTES.TERMS_OF_SERVICE} className="text-primary-600 hover:text-primary-700">Terms of Service</Link></li>
                <li><Link to={ROUTES.REFUND_POLICY} className="text-primary-600 hover:text-primary-700">Refund Policy</Link></li>
              </ul>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
